<template>
  <article
    class="kds-pricing-card"
    :class="{ 'kds-pricing-card--featured': featured }"
  >
    <KdsBadge
      v-if="featured && badge"
      tone="live"
      class="kds-pricing-card__badge"
    >
      {{ badge }}
    </KdsBadge>
    <span class="kds-pricing-card__tier">{{ tier }}</span>
    <div class="kds-pricing-card__price">
      <Transition name="price-fade" mode="out-in">
        <span :key="price" class="kds-pricing-card__price-value">{{ price }}</span>
      </Transition>
      <span v-if="suffix" class="kds-pricing-card__price-suffix">{{ suffix }}</span>
    </div>
    <KdsButton
      class="kds-pricing-card__cta"
      :class="{ 'kds-pricing-card__cta--disabled': ctaDisabled }"
      :variant="featured ? 'signal' : 'primary'"
      size="md"
      :aria-disabled="ctaDisabled || undefined"
      @click="!ctaDisabled && $emit('cta-click')"
      @keydown.enter.prevent="!ctaDisabled && $emit('cta-click')"
      @keydown.space.prevent="!ctaDisabled && $emit('cta-click')"
    >
      {{ ctaLabel }}
    </KdsButton>
    <p v-if="ctaNote" class="kds-pricing-card__cta-note">{{ ctaNote }}</p>
    <ul class="kds-pricing-card__features">
      <li
        v-for="feature in features"
        :key="feature"
        class="kds-pricing-card__feature"
      >
        <KdsIcon name="check" :size="16" class="kds-pricing-card__feature-icon" />
        <span>{{ feature }}</span>
      </li>
    </ul>
    <slot name="footer" />
  </article>
</template>

<script setup lang="ts">
import KdsBadge from '@/kds/primitives/KdsBadge.vue';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

interface Props {
  tier: string;
  price: string;
  ctaLabel: string;
  features: string[];
  suffix?: string;
  featured?: boolean;
  badge?: string;
  ctaDisabled?: boolean;
  ctaNote?: string;
}

withDefaults(defineProps<Props>(), {
  suffix: undefined,
  featured: false,
  badge: undefined,
  ctaDisabled: false,
  ctaNote: undefined
});

defineEmits<{
  (e: 'cta-click'): void;
}>();
</script>

<style scoped lang="scss">
.kds-pricing-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-7);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-5);
  color: var(--ink);
  transition: transform var(--d-base) var(--ease-out), box-shadow var(--d-base) var(--ease-out), border-color var(--d-base) var(--ease-out);

  &:hover {
    box-shadow: var(--shadow-2);
    border-color: var(--mist-2);
  }

  &--featured {
    background: var(--ink);
    color: var(--paper);
    border-color: var(--ink);
  }

  &__badge {
    position: absolute;
    top: var(--space-4);
    right: var(--space-4);
  }

  &__tier {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  &--featured .kds-pricing-card__tier {
    color: var(--stone-2);
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }

  &__price-value {
    font-family: var(--font-display);
    font-size: var(--fs-display-l);
    line-height: var(--lh-display-l);
    letter-spacing: var(--ls-display-l);
  }

  &__price-suffix {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__cta {
    width: 100%;

    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__cta-note {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone-2);
    text-align: center;
    margin-top: var(--space-2);
  }

  &__features {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  &__feature {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
    font-size: var(--fs-body-sm);
    line-height: var(--lh-body-sm);
  }

  &__feature-icon {
    color: var(--ok);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &--featured .kds-pricing-card__feature-icon {
    color: var(--signal);
  }
}

// Price transition
.price-fade-enter-active,
.price-fade-leave-active {
  transition: opacity 120ms var(--ease-out);
}

.price-fade-enter-from,
.price-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .price-fade-enter-active,
  .price-fade-leave-active {
    transition: none !important;
  }
}
</style>
